import { useState, useEffect, useMemo } from "react";
import { getUserTable, addUser, getAllUserType, getAllTerminals } from "../model/UserModel";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";
import { UserKeys } from "../../assets/VarConstants";

const UserManagementVM = () => {
    const { handleToastOpen } = useToast();

    // Data states
    const [tableData, setTableData] = useState([]);
    const [userTypes, setUserTypes] = useState([]);
    const [userTerminals, setUserTerminals] = useState([]);

    // UI states
    const [activeTab, setActiveTab] = useState('users');
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingUser, setEditingUser] = useState(null);

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [jumpToPage, setJumpToPage] = useState('');
    const itemsPerPage = 25;

    // Inline editing states
    const [inlineEditingUser, setInlineEditingUser] = useState(null);
    const [inlineEditData, setInlineEditData] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    // Form state
    const [newUser, setNewUser] = useState({
        [UserKeys.UPDATE_USER_ID]: null,
        [UserKeys.USER_EMAIL]: '',
        [UserKeys.USER_NAME]: '',
        [UserKeys.USER_ROLE_ID]: '',
        [UserKeys.TERMINAL_ID]: '',
        [UserKeys.USER_STATUS]: 1,
        isUpdate: false
    });

    // Fetch data on component mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [userTableResponse, userTypesResponse, userTerminalsResponse] = await Promise.all([
                    getUserTable(),
                    getAllUserType(),
                    getAllTerminals()
                ]);

                setTableData(userTableResponse.data);
                setUserTypes(userTypesResponse.data);
                setUserTerminals(userTerminalsResponse.data);
            } catch (error) {
                console.error("Error fetching data:", error);
                handleToastOpen("error", error.message);
            } 
        }

        fetchData();
    }, [handleToastOpen]);

    // Reset pagination when search term changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    // Filter users based on search
    const filteredUsers = useMemo(() => {
        const term = searchTerm.toLowerCase();
        return tableData.filter(user =>
            Object.values(user).some(value =>
                String(value || "").toLowerCase().includes(term)
            )
        );
    }, [tableData, searchTerm]);

    // Pagination calculations
    const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentUsers = filteredUsers.slice(startIndex, endIndex);

    // Pagination info
    const paginationInfo = {
        currentPage,
        totalPages,
        totalItems: filteredUsers.length,
        startIndex: startIndex + 1,
        endIndex: Math.min(endIndex, filteredUsers.length),
        itemsPerPage
    };

    // Enhanced validation function in your UserManagementVM
    const validateUserData = (userData) => {
        // //console.log("Validating user data:", userData);

        const errors = [];

        if (!userData[UserKeys.USER_EMAIL] || userData[UserKeys.USER_EMAIL].trim() === '') {
            errors.push('Email is required');
        }

        if (!userData[UserKeys.USER_NAME] || userData[UserKeys.USER_NAME].trim() === '') {
            errors.push('User name is required');
        }

        if (!userData[UserKeys.USER_ROLE_ID] ||
            userData[UserKeys.USER_ROLE_ID] === '' ||
            userData[UserKeys.USER_ROLE_ID] === null ||
            userData[UserKeys.USER_ROLE_ID] === undefined) {
            errors.push('User role is required');
        }

        if (!userData[UserKeys.TERMINAL_ID] ||
            userData[UserKeys.TERMINAL_ID] === '' ||
            userData[UserKeys.TERMINAL_ID] === null ||
            userData[UserKeys.TERMINAL_ID] === undefined) {
            errors.push('Terminal is required');
        }

        // Enhanced status validation
        const status = userData[UserKeys.USER_STATUS];
        if (status === '' || status === null || status === undefined) {
            errors.push('Status is required');
        } else {
            // Validate that status is either 0 or 1 (number or string)
            const statusNum = Number(status);
            if (isNaN(statusNum) || (statusNum !== 0 && statusNum !== 1)) {
                errors.push('Status must be either Active (1) or Inactive (0)');
            }
        }

        //console.log("Validation errors:", errors);
        return errors;
    };

    // Enhanced handleAddUser function
    const handleAddUser = async () => {
        //console.log("handleAddUser called with newUser:", newUser);

        const validationErrors = validateUserData(newUser);

        if (validationErrors.length > 0) {
            //console.log("Validation failed:", validationErrors);
            const errorMessage = validationErrors.join(', ');
            handleToastOpen("error", `Please fix the following issues: ${errorMessage}`);
            return;
        }

        setIsLoading(true);
        try {
            const userDataToSend = {
                ...newUser,
                [UserKeys.USER_EMAIL]: newUser[UserKeys.USER_EMAIL].trim(),
                [UserKeys.USER_NAME]: newUser[UserKeys.USER_NAME].trim(),
                [UserKeys.USER_ROLE_ID]: Number(newUser[UserKeys.USER_ROLE_ID]) || newUser[UserKeys.USER_ROLE_ID],
                [UserKeys.TERMINAL_ID]: Number(newUser[UserKeys.TERMINAL_ID]) || newUser[UserKeys.TERMINAL_ID],
                // Ensure status is sent as a number
                [UserKeys.USER_STATUS]: Number(newUser[UserKeys.USER_STATUS]),
            };

            if (newUser.isUpdate && newUser[UserKeys.UPDATE_USER_ID]) {
                userDataToSend[UserKeys.UPDATE_USER_ID] = newUser[UserKeys.UPDATE_USER_ID];
            }

            //console.log("Sending to API:", userDataToSend);

            const response = await addUser(userDataToSend);

            //console.log("API response:", response);

            // Refresh the table data
            const userTableResponse = await getUserTable();
            setTableData(userTableResponse.data);

            // Reset form
            setNewUser({
                [UserKeys.UPDATE_USER_ID]: null,
                [UserKeys.USER_EMAIL]: '',
                [UserKeys.USER_NAME]: '',
                [UserKeys.USER_ROLE_ID]: '',
                [UserKeys.TERMINAL_ID]: '',
                [UserKeys.USER_STATUS]: 1, // Default to active
                isUpdate: false
            });
            setShowAddForm(false);
            setEditingUser(null);

            const successMessage = newUser.isUpdate ? 'User updated successfully' : 'User added successfully';
            handleToastOpen("success", response.data?.message || successMessage);
        } catch (error) {
            console.error("Error adding/updating user:", error);
            console.error("Error details:", error.response);

            let errorMessage = "An error occurred while saving the user";

            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

            handleToastOpen("error", errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearchChange = (term) => {
        setSearchTerm(term);
    };

    const handleShowAddForm = () => {
        setShowAddForm(true);
        setEditingUser(null);
        setNewUser({
            [UserKeys.UPDATE_USER_ID]: null,
            [UserKeys.USER_EMAIL]: '',
            [UserKeys.USER_NAME]: '',
            [UserKeys.USER_ROLE_ID]: '',
            [UserKeys.TERMINAL_ID]: '',
            [UserKeys.USER_STATUS]: 1,
            isUpdate: false
        });
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    // Quick jump pagination handlers
    const handleQuickJump = () => {
        const page = parseInt(jumpToPage);
        if (page >= 1 && page <= totalPages) {
            handlePageChange(page);
            setJumpToPage('');
        }
    };

    const handleJumpInputKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleQuickJump();
        }
    };

    const handleJumpToPageChange = (value) => {
        setJumpToPage(value);
    };

    // Inline editing handlers
    const handleStartInlineEdit = (user) => {
        //console.log("Starting inline edit for user:", user);
        setInlineEditingUser(user.user_id);
        setInlineEditData({
            [UserKeys.USER_NAME]: user.user_name || '',
            [UserKeys.USER_EMAIL]: user.email || '',
            [UserKeys.USER_ROLE_ID]: user.user_role_id || '',
            [UserKeys.TERMINAL_ID]: user.user_terminal_id || user.terminal_id || '',
            [UserKeys.USER_STATUS]: user.user_status || ''
        });
    };

    const handleCancelInlineEdit = () => {
        setInlineEditingUser(null);
        setInlineEditData({});
    };

    const handleSaveInlineEdit = async () => {
        //console.log("Saving inline edit for user:", inlineEditingUser);
        //console.log("Inline edit data:", inlineEditData);

        try {
            const success = await handleInlineUserUpdate(inlineEditingUser, inlineEditData);

            if (success) {
                setInlineEditingUser(null);
                setInlineEditData({});
            }
        } catch (error) {
            console.error("Error in handleSaveInlineEdit:", error);
        }
    };

    const handleInlineDataChange = (field, value) => {
        //console.log(`Inline field ${field} changed to:`, value);
        setInlineEditData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleDeleteUser = (id) => {
        //console.log('Deleting user with ID:', id);
        // TODO: Implement delete user API call
    };

    const handleEditUser = (user) => {
        //console.log("Editing user:", user);

        setEditingUser(user.user_id);
        setShowAddForm(true);

        const editUserData = {
            [UserKeys.UPDATE_USER_ID]: user.user_id,
            [UserKeys.USER_EMAIL]: user.email || '',
            [UserKeys.USER_NAME]: user.user_name || '',
            [UserKeys.USER_ROLE_ID]: user.user_role_id || user.role_id || '',
            [UserKeys.TERMINAL_ID]: user.user_terminal_id || user.terminal_id || '',
            [UserKeys.USER_STATUS]: user.user_status || 1,
            isUpdate: true
        };

        //console.log("Setting edit data:", editUserData);
        setNewUser(editUserData);
    };

    const handleUpdateUser = async () => {
        //console.log("handleUpdateUser called with current newUser:", newUser);
        if (!newUser.isUpdate) {
            //console.log("Setting isUpdate to true");
            setNewUser(prev => ({ ...prev, isUpdate: true }));

            setTimeout(() => {
                handleAddUser();
            }, 100);
        } else {
            await handleAddUser();
        }
    };

    const handleCancelForm = () => {
        setShowAddForm(false);
        setEditingUser(null);
        setNewUser({
            [UserKeys.UPDATE_USER_ID]: null,
            [UserKeys.USER_EMAIL]: '',
            [UserKeys.USER_NAME]: '',
            [UserKeys.USER_ROLE_ID]: '',
            [UserKeys.TERMINAL_ID]: '',
            [UserKeys.USER_STATUS]: 1,
            isUpdate: false
        });
    };

    const handleNewUserChange = (field, value) => {
        //console.log(`Field ${field} changed to:`, value);
        setNewUser(prev => ({ ...prev, [field]: value }));
    };

    const handleInlineUserUpdate = async (userId, updateData) => {
        //console.log("handleInlineUserUpdate called:", { userId, updateData });

        setIsLoading(true);
        try {
            const validationErrors = validateUserData(updateData);

            if (validationErrors.length > 0) {
                const errorMessage = validationErrors.join(', ');
                handleToastOpen("error", `Please fix the following issues: ${errorMessage}`);
                return false;
            }

            const userDataToSend = {
                [UserKeys.UPDATE_USER_ID]: userId,
                [UserKeys.USER_EMAIL]: updateData[UserKeys.USER_EMAIL].trim(),
                [UserKeys.USER_NAME]: updateData[UserKeys.USER_NAME].trim(),
                [UserKeys.USER_ROLE_ID]: Number(updateData[UserKeys.USER_ROLE_ID]) || updateData[UserKeys.USER_ROLE_ID],
                [UserKeys.TERMINAL_ID]: Number(updateData[UserKeys.TERMINAL_ID]) || updateData[UserKeys.TERMINAL_ID],
                [UserKeys.USER_STATUS]: Number(updateData[UserKeys.USER_STATUS]),
                isUpdate: true
            };

            //console.log("Sending inline update to API:", userDataToSend);

            const response = await addUser(userDataToSend);

            //console.log("Inline update API response:", response);

            const userTableResponse = await getUserTable();
            setTableData(userTableResponse.data);

            handleToastOpen("success", response.data?.message || 'User updated successfully');
            return true;
        } catch (error) {
            console.error("Error in inline user update:", error);

            let errorMessage = "An error occurred while updating the user";
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

            handleToastOpen("error", errorMessage);
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    const getPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= 4; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 3; i <= totalPages; i++) {
                    pages.push(i);
                }
            } else {
                pages.push(1);
                pages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            }
        }

        return pages;
    };

    return {
        // Data
        tableData,
        currentUsers,
        userTypes,
        userTerminals,
        filteredUsers,

        // States
        activeTab,
        searchTerm,
        showAddForm,
        editingUser,
        newUser,
        jumpToPage,
        inlineEditingUser,
        inlineEditData,

        // Pagination
        paginationInfo,
        totalPages,

        // Event handlers
        handleSearchChange,
        handleShowAddForm,
        handlePageChange,
        handlePreviousPage,
        handleNextPage,
        handleQuickJump,
        handleJumpInputKeyPress,
        handleJumpToPageChange,
        handleStartInlineEdit,
        handleCancelInlineEdit,
        handleSaveInlineEdit,
        handleInlineDataChange,
        handleAddUser,
        handleDeleteUser,
        handleEditUser,
        handleUpdateUser,
        handleCancelForm,
        handleNewUserChange,
        handleInlineUserUpdate,

        // Utility functions
        getPageNumbers,
        validateUserData,
        isLoading
    };
};

export default UserManagementVM;